/**
 * TasksKpiStrip — clickable KPI tiles for Task Manager landing.
 *
 * v0.1.52 — REPLACES the legacy "SLA targets 18" un-clickable misfeature.
 * That tile surfaced the catalog row count from /v3/config/sla-targets, which
 * is admin metadata, not an operations metric. It's removed here; SLA-targets
 * the catalog belongs in Admin (and IS rendered there as an admin row).
 *
 * Role gating (per project memory + Bill 2026-04-24):
 *   - Manager / Executive / SuperAdmin → see this strip
 *   - FSR / Supervisor                 → strip is HIDDEN by parent
 *   - Compliance / Auditor             → see strip in read-only mode (display only)
 *
 * Each tile is clickable and routes the operator to a filtered task list.
 * Filter is communicated via callback so the parent can keep tab + chip state
 * in sync (URL search params would also work; we use callback to avoid a
 * router round-trip on what's already a same-page filter).
 */
import { useMemo } from 'react';
import type { TaskRecordDto } from '@retail-ops/shared-types';
import { KpiTile } from '@/components/primitives';
import { deriveSlaState } from './TaskCard';

export type TasksKpiKey =
  | 'open'
  | 'sla-at-risk'
  | 'breached-today'
  | 'overdue'
  | 'unassigned'
  | 'critical';

export interface TasksKpiStripProps {
  tasks: ReadonlyArray<TaskRecordDto>;
  /** Optional snapshot of tasks 24 hours ago to compute deltas. */
  yesterdayTasks?: ReadonlyArray<TaskRecordDto>;
  onSelect: (key: TasksKpiKey) => void;
  selected?: TasksKpiKey | null;
}

interface KpiCount {
  total: number;
  delta: number; // signed: positive = went up
}

function diff(today: number, yesterday: number): number {
  return today - yesterday;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isBreachedToday(t: TaskRecordDto): boolean {
  const sla = deriveSlaState(t);
  if (sla.state !== 'breached') return false;
  if (!t.dueDate) return false;
  return t.dueDate.slice(0, 10) === todayIso();
}

function isUnassigned(t: TaskRecordDto): boolean {
  const a = (t.assignedTo ?? '').trim().toLowerCase();
  return a === '' || a === 'unassigned';
}

function isOverdue(t: TaskRecordDto): boolean {
  if (!t.dueDate) return false;
  return new Date(t.dueDate).getTime() < Date.now();
}

function isCritical(t: TaskRecordDto): boolean {
  return t.severity === 'critical' || String(t.priority).toLowerCase() === 'urgent' || String(t.priority).toLowerCase() === 'critical';
}

export function TasksKpiStrip({
  tasks,
  yesterdayTasks,
  onSelect,
  selected,
}: TasksKpiStripProps): JSX.Element {
  const counts = useMemo<Record<TasksKpiKey, KpiCount>>(() => {
    const calc = (list: ReadonlyArray<TaskRecordDto>): Record<TasksKpiKey, number> => ({
      'open': list.filter((t) => t.status !== 'Completed' && t.status !== 'Cancelled').length,
      'sla-at-risk': list.filter((t) => deriveSlaState(t).state === 'at-risk').length,
      'breached-today': list.filter(isBreachedToday).length,
      'overdue': list.filter((t) => isOverdue(t) && t.status !== 'Completed').length,
      'unassigned': list.filter(isUnassigned).length,
      'critical': list.filter(isCritical).length,
    });
    const today = calc(tasks);
    const yesterday = yesterdayTasks ? calc(yesterdayTasks) : null;
    const out = {} as Record<TasksKpiKey, KpiCount>;
    (Object.keys(today) as TasksKpiKey[]).forEach((k) => {
      out[k] = {
        total: today[k],
        delta: yesterday ? diff(today[k], yesterday[k]) : 0,
      };
    });
    return out;
  }, [tasks, yesterdayTasks]);

  const tile = (key: TasksKpiKey, label: string, severity?: 'urgent' | 'warn' | 'default'): JSX.Element => {
    const c = counts[key];
    const isSelected = selected === key;
    const sev = c.total > 0 && severity === 'urgent'
      ? 'urgent'
      : c.total > 0 && severity === 'warn'
        ? 'warn'
        : 'default';
    return (
      <button
        type="button"
        className={`task-kpi-btn ${isSelected ? 'is-selected' : ''}`}
        onClick={() => onSelect(key)}
        aria-pressed={isSelected ? 'true' : 'false'}
        aria-label={`Filter by ${label} — ${c.total} tasks`}
      >
        <KpiTile
          label={label}
          value={c.total}
          severity={sev}
          {...(c.delta !== 0
            ? {
                delta: {
                  dir: c.delta > 0 ? 'up' : 'down',
                  text: `${Math.abs(c.delta)} vs yesterday`,
                },
              }
            : {})}
        />
      </button>
    );
  };

  return (
    <section className="task-kpi-strip" aria-label="Task KPIs">
      {tile('open', 'Open tasks')}
      {tile('sla-at-risk', 'SLA at risk', 'warn')}
      {tile('breached-today', 'Breached today', 'urgent')}
      {tile('overdue', 'Overdue', 'urgent')}
      {tile('unassigned', 'Unassigned', 'warn')}
      {tile('critical', 'Critical priority', 'urgent')}
    </section>
  );
}
